import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, ShieldOff, ClipboardList } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useQuery } from '@tanstack/react-query';
import { PRIVACY_NOTICE_VERSION } from '@/pages/PrivacyPolicy';
import { formatLocalizedDate } from '@/lib/localizedDate';

interface ConsentHistorySectionProps {
  userId: string | undefined;
  portal: 'hire' | 'seek';
}

const TOUCHPOINT_LABEL_KEYS: Record<string, string> = {
  seek_signup: 'common.consentHistory.touchpoints.seek_signup',
  public_apply: 'common.consentHistory.touchpoints.public_apply',
  interview_start: 'common.consentHistory.touchpoints.interview_start',
  cv_scoring: 'common.consentHistory.touchpoints.cv_scoring',
  cv_generate: 'common.consentHistory.touchpoints.cv_generate',
  cv_review: 'common.consentHistory.touchpoints.cv_review',
  seek_apply: 'common.consentHistory.touchpoints.seek_apply',
  seek_cv_parse: 'common.consentHistory.touchpoints.seek_cv_parse',
  practice_interview: 'common.consentHistory.touchpoints.practice_interview',
  job_match: 'common.consentHistory.touchpoints.job_match',
};

/**
 * Read-only consent history section for settings pages.
 * Shows all consent acknowledgements for the current user without
 * any ability to modify them (withdrawal is handled separately in ConsentManagementCard).
 */
export function ConsentHistorySection({ userId, portal }: ConsentHistorySectionProps) {
  const { t, i18n } = useTranslation();
  const client = portal === 'seek' ? seekSupabase : supabase;
  const formatConsentDate = (value: string) => formatLocalizedDate(value, 'PP', i18n.language);
  const getTouchpointLabel = (touchpoint: string) => {
    const labelKey = TOUCHPOINT_LABEL_KEYS[touchpoint];
    return labelKey ? t(labelKey) : touchpoint;
  };

  const { data: consents, isLoading } = useQuery({
    queryKey: ['consent-history', userId, portal],
    queryFn: async () => {
      const { data, error } = await client
        .from('consent_acknowledgements')
        .select('id, consent_type, touchpoint, acknowledged_at, revoked_at, notice_version_id')
        .eq('user_id', userId!)
        .order('acknowledged_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const activeConsents = consents?.filter(c => !c.revoked_at) ?? [];
  const revokedConsents = consents?.filter(c => c.revoked_at) ?? [];
  const totalConsents = (consents ?? []).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary" />
          {t('common.consentHistory.title')}
        </CardTitle>
        <CardDescription>
          {t('common.consentHistory.description', { version: PRIVACY_NOTICE_VERSION })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {totalConsents === 0 && (
          <p className="text-sm text-muted-foreground">{t('common.consentHistory.empty')}</p>
        )}

        {activeConsents.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('common.consentHistory.activeCount', { count: activeConsents.length })}</p>
            {activeConsents.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {getTouchpointLabel(c.touchpoint)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {c.consent_type.replace(/_/g, ' ')} · {formatConsentDate(c.acknowledged_at)}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-200 shrink-0">
                  <ShieldCheck className="w-3 h-3 me-1" />
                  {t('common.active')}
                </Badge>
              </div>
            ))}
          </div>
        )}

        {revokedConsents.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('common.consentHistory.withdrawnCount', { count: revokedConsents.length })}</p>
            {revokedConsents.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border opacity-60">
                <div className="min-w-0">
                  <p className="text-sm text-foreground">
                    {getTouchpointLabel(c.touchpoint)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {c.consent_type.replace(/_/g, ' ')} · {t('common.consentHistory.granted')} {formatConsentDate(c.acknowledged_at)} ·
                    {t('common.consentHistory.withdrawn')} {formatConsentDate(c.revoked_at!)}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/20 shrink-0">
                  <ShieldOff className="w-3 h-3 me-1" />
                  {t('common.consentHistory.withdrawn')}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
